import { useEffect, useRef, useState } from "react";
import { faderDbLabel, isAudible, panLabel, UNITY_VOLUME } from "@shared/mixer.js";
import type { TrackField, TrackFieldValue } from "@shared/sync/state.js";
import type { Lane } from "@web-shared/lib/arrangement";
import { cn } from "@web-shared/lib/utils";
import { PatchSelect, type LaneAudioState } from "@web-shared/components/ArrangementView";

/** What the mixer needs to move the Set's faders. Absent ⇒ read-only strips. */
export interface MixerEditState {
  onTrackField: (laneId: string, field: TrackField, value: TrackFieldValue) => void;
  /** The Set's master fader + pan — proposed like a lane's mixer fields. */
  /** Either half alone: the page merges it onto the latest room master, so
   * a pan turned right after a fader move keeps the move. */
  onMaster?: (patch: { volume?: number; pan?: number }) => void;
  /** A lane's send to return `index`, and a return track's own fields. */
  onSend?: (laneId: string, index: number, level: number) => void;
  onReturnField?: (index: number, patch: Partial<ReturnTrack>) => void;
  busy: boolean;
}

export interface ReturnTrack {
  name: string;
  mute: boolean;
  solo: boolean;
  volume: number;
  pan: number;
}

export interface MeterSource {
  /** Post-fader peak for a lane, 0..1+ (over 1 = clipping). */
  level: (laneId: string) => number;
  master: () => number;
  /** Meters only move while something plays; when false they decay to rest. */
  active: boolean;
}

interface Props {
  lanes: Lane[];
  audio?: LaneAudioState;
  edit?: MixerEditState;
  meters: MeterSource;
  /** Master (playback) volume, 0..1, and its setter — a listening control. */
  masterVolume: number;
  onMasterVolume: (v: number) => void;
  /** The Set's master fader + pan (room state), when the room carries it. */
  master?: { volume: number; pan: number };
  /** The Set's return tracks and each lane's send levels, when the room carries them. */
  returns?: ReturnTrack[];
  sendsByLane?: Record<string, number[]>;
}

const STRIP_W = 64;
const FADER_H = 110;
/** Meter ballistics: instant attack, this fraction retained per frame. */
const METER_DECAY = 0.85;
/** Peak-hold indicator lifetime, frames. */
const PEAK_HOLD_FRAMES = 45;

/**
 * Live's mixer, drawn: one strip per lane — meter, fader, pan, the Set's
 * M/S/● and the browser's own m/s — plus a master strip. Faders and pans
 * are the Set's (proposed on release, exactly as the lane header's are);
 * meters are the browser's playback, post-fader, so what moves is what you
 * hear. Playback-only meters never touch the wire.
 */
export function MixerPanel({ lanes, audio, edit, meters, masterVolume, onMasterVolume, master, returns, sendsByLane }: Props) {
  return (
    <section className="overflow-x-auto rounded-lg border border-border bg-card p-2">
      <div className="flex items-end gap-1.5" style={{ width: "max-content" }}>
        {lanes.map((lane) => (
          <Strip key={lane.id} lane={lane} audio={audio} edit={edit} meters={meters} returns={returns} sends={sendsByLane?.[lane.id]} />
        ))}
        {returns?.map((rt, i) => (
          <ReturnStrip key={`return-${i}`} index={i} track={rt} edit={edit} />
        ))}
        <MasterStrip meters={meters} volume={masterVolume} onVolume={onMasterVolume} master={master} edit={edit} />
      </div>
    </section>
  );
}

function Strip({
  lane,
  audio,
  edit,
  meters,
  returns,
  sends,
}: {
  lane: Lane;
  audio: LaneAudioState | undefined;
  edit: MixerEditState | undefined;
  meters: MeterSource;
  returns: ReturnTrack[] | undefined;
  sends: number[] | undefined;
}) {
  const soloing = audio ? audio.soloed.size > 0 : false;
  const audible = audio ? isAudible(audio.muted.has(lane.id), audio.soloed.has(lane.id), soloing) : true;
  return (
    <div
      className={cn("flex flex-col items-center gap-1 rounded-md border border-border/60 bg-background px-1 py-1.5", !audible && "opacity-60")}
      style={{ width: STRIP_W }}
    >
      <span className="w-full truncate text-center text-[10px]" title={lane.name}>
        {lane.kind === "midi" ? "♪ " : ""}
        {lane.name || "(unnamed)"}
      </span>
      <div className="flex items-end gap-1">
        <Meter read={() => meters.level(lane.id)} active={meters.active} height={FADER_H} />
        <VerticalFader
          value={lane.volume}
          min={0}
          max={1}
          height={FADER_H}
          disabled={!edit || edit.busy}
          title={edit ? "Mixer volume — released value is proposed to the Set" : `Mixer volume: ${faderDbLabel(lane.volume)}`}
          onCommit={(v) => edit?.onTrackField(lane.id, "volume", v)}
          reset={UNITY_VOLUME}
        />
      </div>
      <span className="tabular text-[9px] text-muted-foreground">{faderDbLabel(lane.volume)}</span>
      <PanKnob
        value={lane.pan}
        disabled={!edit || edit.busy}
        onCommit={(v) => edit?.onTrackField(lane.id, "pan", v)}
      />
      {returns && returns.length > 0 && (
        <div className="flex w-full flex-col gap-0.5" title="Sends to the Set's return tracks (heard dry here — Live's return effects don't travel)">
          {returns.map((rt, i) => (
            <SendSlider
              key={i}
              label={String.fromCharCode(65 + i)}
              title={`Send ${String.fromCharCode(65 + i)} — ${rt.name || "(unnamed)"}`}
              value={sends?.[i] ?? 0}
              disabled={!edit?.onSend || edit.busy}
              onCommit={(v) => edit?.onSend?.(lane.id, i, v)}
            />
          ))}
        </div>
      )}
      <span className="flex items-center gap-0.5">
        {edit ? (
          <>
            <Toggle label="M" tone="removed" active={lane.mute} title="Mute in the Set" onClick={() => edit.onTrackField(lane.id, "mute", !lane.mute)} />
            <Toggle label="S" tone="changed" active={lane.solo} title="Solo in the Set" onClick={() => edit.onTrackField(lane.id, "solo", !lane.solo)} />
            <Toggle label="●" tone="removed" active={lane.arm} title="Arm in the Set" onClick={() => edit.onTrackField(lane.id, "arm", !lane.arm)} />
          </>
        ) : (
          <>
            {lane.mute && <Toggle label="M" tone="removed" active title="Muted in the Set" />}
            {lane.solo && <Toggle label="S" tone="changed" active title="Soloed in the Set" />}
          </>
        )}
      </span>
      {audio && (
        <span className="flex items-center gap-0.5">
          <Toggle label="m" tone="removed" active={audio.muted.has(lane.id)} title="Mute in playback only" onClick={() => audio.onMute(lane.id, !audio.muted.has(lane.id))} />
          <Toggle label="s" tone="changed" active={audio.soloed.has(lane.id)} title="Solo in playback only" onClick={() => audio.onSolo(lane.id, !audio.soloed.has(lane.id))} />
        </span>
      )}
      {audio && lane.kind === "midi" && <PatchSelect laneId={lane.id} audio={audio} className="w-full" />}
    </div>
  );
}

/**
 * The master strip: the SET's master fader and pan when the room carries
 * them (proposed on release, like a lane's), with the browser's own
 * listening volume as a small slider beneath; the listening fader alone
 * for rooms and pages without a synced master.
 */
function MasterStrip({
  meters,
  volume,
  onVolume,
  master,
  edit,
}: {
  meters: MeterSource;
  volume: number;
  onVolume: (v: number) => void;
  master: { volume: number; pan: number } | undefined;
  edit: MixerEditState | undefined;
}) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-md border border-[var(--primary)]/40 bg-background px-1 py-1.5" style={{ width: STRIP_W }}>
      <span className="text-[10px] font-medium">Master</span>
      <div className="flex items-end gap-1">
        <Meter read={meters.master} active={meters.active} height={FADER_H} />
        {master ? (
          <VerticalFader
            value={master.volume}
            min={0}
            max={1}
            height={FADER_H}
            disabled={!edit?.onMaster || edit.busy}
            title={edit?.onMaster ? "The Set's master fader — released value is proposed to the Set" : `The Set's master fader: ${faderDbLabel(master.volume)}`}
            onCommit={(v) => edit?.onMaster?.({ volume: v })}
            reset={UNITY_VOLUME}
          />
        ) : (
          <VerticalFader
            value={volume}
            min={0}
            max={1}
            height={FADER_H}
            disabled={false}
            title="Playback volume — this browser only"
            onCommit={onVolume}
            onInput={onVolume}
            reset={1}
          />
        )}
      </div>
      {master ? (
        <>
          <span className="tabular text-[9px] text-muted-foreground">{faderDbLabel(master.volume)}</span>
          <PanKnob value={master.pan} disabled={!edit?.onMaster || edit.busy} onCommit={(p) => edit?.onMaster?.({ pan: p })} />
          <label className="flex w-full items-center gap-1 text-[9px] text-muted-foreground" title="Playback volume — this browser only">
            <span>ear</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={(e) => onVolume(Number(e.target.value))}
              onKeyDown={(e) => e.stopPropagation()}
              className="h-1 w-full cursor-pointer accent-[var(--primary)]"
              aria-label="Playback volume"
            />
          </label>
        </>
      ) : (
        <span className="tabular text-[9px] text-muted-foreground">{Math.round(volume * 100)}%</span>
      )}
    </div>
  );
}

/**
 * A meter, drawn per animation frame from the engine's analyser: instant
 * attack, exponential fall, a peak-hold tick, red past full scale. Its own
 * canvas so twelve of them cost twelve tiny fills, not React renders.
 */
/** Shared with the play screen's mixer: one set of meter ballistics, so a
 * level reads the same wherever it is drawn. */
export function Meter({ read, active, height }: { read: () => number; active: boolean; height: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  // The reader arrives as a fresh closure per render (the page re-renders
  // per frame while playing); keyed on it, the effect would reset the
  // ballistics every frame. Read through a ref instead.
  const readRef = useRef(read);
  readRef.current = read;
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const g = canvas.getContext("2d");
    if (!g) return;
    const dpr = window.devicePixelRatio || 1;
    const w = 8;
    canvas.width = w * dpr;
    canvas.height = height * dpr;
    g.scale(dpr, dpr);
    let shown = 0;
    let hold = 0;
    let holdFrames = 0;
    let frame = 0;
    let running = true;
    const draw = () => {
      if (!running) return;
      const level = active ? readRef.current() : 0;
      shown = Math.max(level, shown * METER_DECAY);
      if (level >= hold) {
        hold = level;
        holdFrames = PEAK_HOLD_FRAMES;
      } else if (--holdFrames <= 0) {
        hold = Math.max(0, hold - 0.02);
      }
      g.clearRect(0, 0, w, height);
      g.fillStyle = "rgba(255,255,255,0.06)";
      g.fillRect(0, 0, w, height);
      // dB-ish scale: full scale at the top, −40 dB at the bottom.
      const y = (v: number) => height - Math.max(0, Math.min(1, 1 + (20 * Math.log10(Math.max(v, 1e-4))) / 40)) * height;
      const top = y(shown);
      const grad = g.createLinearGradient(0, height, 0, 0);
      grad.addColorStop(0, "#4bcc88");
      grad.addColorStop(0.75, "#d8a13a");
      grad.addColorStop(1, "#d4553f");
      g.fillStyle = grad;
      g.fillRect(0, top, w, height - top);
      if (hold > 0.001) {
        g.fillStyle = hold >= 1 ? "#d4553f" : "rgba(255,255,255,0.8)";
        g.fillRect(0, Math.max(0, y(hold) - 1), w, 2);
      }
      if (!active && shown < 0.001 && hold < 0.001) return; // rest: stop redrawing
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => {
      running = false;
      cancelAnimationFrame(frame);
    };
  }, [active, height]);
  return <canvas ref={ref} style={{ width: 8, height }} className="rounded-sm" aria-hidden />;
}

/** A vertical range input. Dragged locally, committed on release. */
export function VerticalFader({
  value,
  min,
  max,
  height,
  disabled,
  title,
  onCommit,
  onInput,
  reset,
}: {
  value: number;
  min: number;
  max: number;
  height: number;
  disabled: boolean;
  title: string;
  onCommit: (v: number) => void;
  /** Continuous updates (the master's local volume wants them). */
  onInput?: (v: number) => void;
  reset: number;
}) {
  const [draft, setDraft] = useState<number | null>(null);
  // A broadcast mid-drag (a collaborator on the same control, or our own
  // last commit echoing) must not wipe the value under the pointer.
  const dragging = useRef(false);
  useEffect(() => {
    if (!dragging.current) setDraft(null);
  }, [value]);
  const commit = () => {
    if (draft === null) return;
    const v = draft;
    setDraft(null);
    if (Math.abs(v - value) > 1e-4) onCommit(v);
  };
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={0.005}
      value={draft ?? value}
      disabled={disabled}
      onChange={(e) => {
        const v = Number(e.target.value);
        setDraft(v);
        onInput?.(v);
      }}
      onPointerDown={() => {
        dragging.current = true;
      }}
      onPointerUp={() => {
        dragging.current = false;
        commit();
      }}
      onPointerCancel={() => {
        // A touch the browser took for a pan never gets a pointerup.
        dragging.current = false;
        commit();
      }}
      onKeyUp={commit}
      onBlur={commit}
      onDoubleClick={() => !disabled && onCommit(reset)}
      onKeyDown={(e) => e.stopPropagation()}
      title={title}
      aria-label={title}
      className="cursor-pointer accent-[var(--primary)]"
      style={{ writingMode: "vertical-lr", direction: "rtl", height, width: 16 }}
    />
  );
}

export function PanKnob({ value, disabled, onCommit }: { value: number; disabled: boolean; onCommit: (v: number) => void }) {
  const [draft, setDraft] = useState<number | null>(null);
  // A broadcast mid-drag (a collaborator on the same control, or our own
  // last commit echoing) must not wipe the value under the pointer.
  const dragging = useRef(false);
  useEffect(() => {
    if (!dragging.current) setDraft(null);
  }, [value]);
  const commit = () => {
    if (draft === null) return;
    const v = draft;
    setDraft(null);
    if (Math.abs(v - value) > 1e-4) onCommit(v);
  };
  return (
    <span className="flex w-full items-center gap-1" title="Pan — released value is proposed to the Set; double-click centres">
      <input
        type="range"
        min={-1}
        max={1}
        step={0.01}
        value={draft ?? value}
        disabled={disabled}
        onChange={(e) => setDraft(Number(e.target.value))}
        onPointerDown={() => {
        dragging.current = true;
      }}
      onPointerUp={() => {
        dragging.current = false;
        commit();
      }}
      onPointerCancel={() => {
        // A touch the browser took for a pan never gets a pointerup.
        dragging.current = false;
        commit();
      }}
        onKeyUp={commit}
        onBlur={commit}
        onDoubleClick={() => !disabled && onCommit(0)}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label="Pan"
        className="h-1 min-w-0 flex-1 cursor-pointer accent-[var(--primary)]"
      />
      <span className="tabular w-6 text-[9px] text-muted-foreground">{panLabel(draft ?? value)}</span>
    </span>
  );
}

function Toggle({
  label,
  tone,
  active,
  title,
  onClick,
}: {
  label: string;
  tone: "removed" | "changed";
  active: boolean;
  title: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={!onClick}
      className={cn("rounded px-1 text-[9px] font-bold leading-4", active ? "" : "text-muted-foreground hover:text-foreground")}
      style={active ? { backgroundColor: `color-mix(in srgb, var(--${tone}) 25%, transparent)`, color: `var(--${tone})` } : undefined}
    >
      {label}
    </button>
  );
}

/** One send level as a thin slider — committed on release, like a fader. */
function SendSlider({ label, title, value, disabled, onCommit }: { label: string; title: string; value: number; disabled: boolean; onCommit: (v: number) => void }) {
  const [draft, setDraft] = useState<number | null>(null);
  // A broadcast mid-drag (a collaborator on the same control, or our own
  // last commit echoing) must not wipe the value under the pointer.
  const dragging = useRef(false);
  useEffect(() => {
    if (!dragging.current) setDraft(null);
  }, [value]);
  const commit = () => {
    if (draft === null) return;
    const v = draft;
    setDraft(null);
    if (Math.abs(v - value) > 1e-6) onCommit(v);
  };
  return (
    <label className="flex items-center gap-1 text-[9px] text-muted-foreground" title={title}>
      <span className="w-2 shrink-0">{label}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={draft ?? value}
        disabled={disabled}
        onChange={(e) => setDraft(Number(e.target.value))}
        onPointerDown={() => {
        dragging.current = true;
      }}
      onPointerUp={() => {
        dragging.current = false;
        commit();
      }}
      onPointerCancel={() => {
        // A touch the browser took for a pan never gets a pointerup.
        dragging.current = false;
        commit();
      }}
        onKeyUp={commit}
        onBlur={commit}
        onKeyDown={(e) => e.stopPropagation()}
        className="h-1 w-full cursor-pointer accent-[var(--primary)] disabled:cursor-default"
        aria-label={title}
      />
    </label>
  );
}

/** A return track's strip: the Set's fader, pan, mute and solo — proposed
 * like a lane's. No meter: a return is heard through the master here. */
function ReturnStrip({ index, track, edit }: { index: number; track: ReturnTrack; edit: MixerEditState | undefined }) {
  const can = edit?.onReturnField !== undefined && !edit.busy;
  const set = (patch: Partial<ReturnTrack>) => edit?.onReturnField?.(index, patch);
  return (
    <div className="flex flex-col items-center gap-1 rounded-md border border-[var(--changed)]/40 bg-background px-1 py-1.5" style={{ width: STRIP_W }}>
      <span className="w-full truncate text-center text-[10px]" title={track.name}>
        ↩ {track.name || `Return ${String.fromCharCode(65 + index)}`}
      </span>
      <div className="flex items-end gap-1">
        <VerticalFader
          value={track.volume}
          min={0}
          max={1}
          height={FADER_H}
          disabled={!can}
          title={can ? "Return volume — released value is proposed to the Set" : `Return volume: ${faderDbLabel(track.volume)}`}
          onCommit={(v) => set({ volume: v })}
          reset={UNITY_VOLUME}
        />
      </div>
      <span className="tabular text-[9px] text-muted-foreground">{faderDbLabel(track.volume)}</span>
      <PanKnob value={track.pan} disabled={!can} onCommit={(v) => set({ pan: v })} />
      <span className="flex items-center gap-0.5">
        <Toggle label="M" tone="removed" active={track.mute} title="Mute the return in the Set" onClick={can ? () => set({ mute: !track.mute }) : undefined} />
        <Toggle label="S" tone="changed" active={track.solo} title="Solo the return in the Set" onClick={can ? () => set({ solo: !track.solo }) : undefined} />
      </span>
    </div>
  );
}
